// src/pages/Signup.js
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/authContext';

const TEACHER_CODE = "derindebatecoach123"; // required for teacher role

export default function Signup() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [email, setEmail] = useState(() => localStorage.getItem('da_last_email') || '');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(Boolean(localStorage.getItem('da_last_email')));

  const [username, setUsername] = useState('');
  const [role, setRole] = useState('student');
  const [teacherCode, setTeacherCode] = useState(''); // 🔑 new field

  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // Redirect if logged in
  useEffect(() => {
    if (user) navigate('/');
  }, [user, navigate]);

  // validations
  const emailValid = useMemo(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()), [email]);
  const pwdValid = useMemo(() => password.length >= 6, [password]);
  const confirmValid = useMemo(() => confirm === password && confirm.length > 0, [confirm, password]);
  const usernameValid = useMemo(() => username.trim().length >= 2, [username]);
  const teacherCodeValid = useMemo(() => {
    if (role !== 'teacher') return true;
    return teacherCode.trim() === TEACHER_CODE;
  }, [role, teacherCode]);

  const formValid = emailValid && pwdValid && confirmValid && usernameValid && teacherCodeValid;

  const handleRememberToggle = () => setRemember((prev) => !prev);

  const createProfileIfPossible = async (userId) => {
    if (!userId) return;
    const { error: profileErr } = await supabase
      .from('profiles')
      .upsert(
        { id: userId, username: username.trim(), role },
        { onConflict: 'id' }
      );
    if (profileErr) console.warn('Profile create/upsert warning:', profileErr.message);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setNotice('');

    if (!formValid) {
      if (role === 'teacher' && teacherCode.trim() !== TEACHER_CODE) {
        setError('Invalid teacher access code.');
        return;
      }
      setError('Please complete all fields correctly.');
      return;
    }

    setLoading(true);
    try {
      const { data, error: signErr } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          emailRedirectTo: window.location.origin,
          data: { username: username.trim(), role },
        },
      });

      if (signErr) {
        setError(signErr.message || 'Sign up failed.');
        setLoading(false);
        return;
      }

      if (remember) localStorage.setItem('da_last_email', email.trim());
      else localStorage.removeItem('da_last_email');

      await createProfileIfPossible(data?.user?.id);

      if (!data.session) {
        setNotice('Check your email to confirm your account, then sign in.');
        setLoading(false);
        return;
      }

      navigate('/');
    } catch {
      setError('Unexpected error during sign up.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError('');
    setOauthLoading(true);
    try {
      const { error: oauthErr } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: window.location.origin },
      });
      if (oauthErr) setError(oauthErr.message || 'Google sign up failed.');
    } catch {
      setError('Unexpected error with Google sign up.');
    } finally {
      setOauthLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] grid place-items-center px-4">
      <div className="w-full max-w-md">
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
          {/* Header */}
          <div className="px-6 pt-6 pb-4">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="Debate Academy" className="h-8 w-8" />
              <h1 className="text-2xl font-bold text-gray-900">Create your account</h1>
            </div>
            <p className="mt-1 text-sm text-gray-600">Join Debate Academy and start training.</p>
          </div>

          {/* Body */}
          <div className="px-6 pb-6">
            {error && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}
            {notice && (
              <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                {notice}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Username */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Display name</label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  placeholder="e.g. Alex Chen"
                />
                {!usernameValid && username.length > 0 && (
                  <p className="mt-1 text-xs text-red-600">Name must be at least 2 characters.</p>
                )}
              </div>

              {/* Role */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Role</label>
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                >
                  <option value="student">Student</option>
                  <option value="teacher">Teacher</option>
                </select>
              </div>

              {/* Teacher Access Code (only when Teacher selected) */}
              {role === 'teacher' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Teacher Access Code</label>
                  <input
                    type="password"
                    value={teacherCode}
                    onChange={(e) => setTeacherCode(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                    placeholder="Enter access code"
                  />
                  {!teacherCodeValid && teacherCode.length > 0 && (
                    <p className="mt-1 text-xs text-red-600">Invalid teacher access code.</p>
                  )}
                </div>
              )}

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  placeholder="name@example.com"
                />
              </div>

              {/* Password + Confirm */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Password</label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  placeholder="Minimum 6 characters"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Confirm password</label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  placeholder="Re-enter your password"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={showPassword}
                  onChange={() => setShowPassword((s) => !s)}
                  className="rounded border-gray-300 text-blue-600"
                />
                <span className="text-sm text-gray-700">Show password</span>
              </div>

              {/* Remember email */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={handleRememberToggle}
                  className="rounded border-gray-300 text-blue-600"
                />
                <span className="text-sm text-gray-700">Remember email</span>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading || !formValid}
                className="w-full rounded-lg bg-blue-600 px-4 py-2 text-white font-medium hover:bg-blue-500 disabled:opacity-50"
              >
                {loading ? 'Creating account…' : 'Sign up'}
              </button>
            </form>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-3 text-xs text-gray-500">Or continue with</span>
              </div>
            </div>

            {/* Google OAuth */}
            <button
              type="button"
              onClick={handleGoogle}
              disabled={oauthLoading}
              className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 hover:bg-gray-50"
            >
              {oauthLoading ? 'Redirecting…' : 'Continue with Google'}
            </button>

            {/* Footer */}
            <p className="mt-6 text-center text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="font-medium text-blue-600 hover:underline">
                Log in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
